import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
    Modal,
    ModalBodyFooter,
    ModalProcessing,
    Button,
    ErrorMessage
} from '../../../../components/custom-essentials';
import { FormikControl, SelectSingle, Check } from '../../../../components/form';
import { fetchBookListsStudents } from '../../../../actions';
import { getDataArray, formatDateApi } from '../../../../components/functions';

const generateRowId = () => `bli-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const itemSchema = Yup.object().shape({
    book: Yup.object().nullable().required('Select a book'),
    dateAssigned: Yup.string().nullable(),
    dateCompleted: Yup.string().nullable(),
    sortOrder: Yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Sort order must be a number')
        .integer('Sort order must be a whole number')
        .min(1, 'Sort order must be at least 1')
});

const validationSchema = Yup.object().shape({
    student: Yup.object().nullable().required('Select a student'),
    name: Yup.string().trim().required('List name is required'),
    notes: Yup.string().nullable(),
    active: Yup.boolean(),
    items: Yup.array().of(itemSchema).min(1, 'Add at least one book to the list')
});

function BookListModal(props) {
    const {
        show = true,
        mode = 'create',
        bookList,
        students = [],
        books = [],
        templates = [],
        templateItems = [],
        onClose,
        fetchBookListsId,
        fetchBookListsStudents,
        createBookList,
        updateBookList,
        deleteBookList,
        fetchBookListTemplatesFull
    } = props;

    const isEditMode = mode === 'edit';
    const isDeleteMode = mode === 'delete';

    const [submitted, setSubmitted] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [deleteProcessing, setDeleteProcessing] = useState(false);

    const [detailLoading, setDetailLoading] = useState(isEditMode);
    const [detailError, setDetailError] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [itemRows, setItemRows] = useState([]);

    // Book list selection for editing
    const [studentBookLists, setStudentBookLists] = useState([]);
    const [selectedBookListId, setSelectedBookListId] = useState(bookList?.id || null);

    const studentOptions = useMemo(() => students.map(student => ({
        value: student.user_id,
        label: `${student.first_name} ${student.last_name}`
    })), [students]);

    const bookOptions = useMemo(() => books.map(book => ({
        value: book.id,
        label: book.title ? `${book.title}${book.author ? ` – ${book.author}` : ''}` : `Book ${book.id}`
    })), [books]);

    // Book list options for the selected student (when editing)
    const bookListOptions = useMemo(() => {
        if (!isEditMode || studentBookLists.length === 0) {
            return [{ value: -1, label: 'Please select a student first...' }];
        }
        return [
            { value: -1, label: 'Select a book list to edit...' },
            ...studentBookLists.map(bl => {
                const dateLabel = bl.date_start ? ` ${formatDateApi(bl.date_start)}` : '';
                const activeLabel = parseInt(bl.active) === 1 ? '' : ' (Inactive)';
                return {
                    value: parseInt(bl.id),
                    label: `${bl.name || 'Unnamed'}${dateLabel}${activeLabel}`.trim(),
                    obj: bl
                };
            })
        ];
    }, [isEditMode, studentBookLists]);

    // Note: useEffect for fetching student book lists moved after formik definition

    // Load book list details when selectedBookListId changes (edit mode)
    useEffect(() => {
        let isMounted = true;
        if (!isEditMode || !selectedBookListId || selectedBookListId === -1) {
            setDetailLoading(false);
            setDetailError(null);
            setDetailData(null);
            return () => { isMounted = false; };
        }

        (async () => {
            setDetailLoading(true);
            setDetailError(null);
            const response = await fetchBookListsId({ id: selectedBookListId });
            if (!isMounted) return;
            if (!response || response.status >= 400) {
                setDetailError(response?.data?.message || 'Unable to load book list details.');
                setDetailLoading(false);
                return;
            }
            setDetailData(response.data || {});
            setDetailLoading(false);
        })();

        return () => { isMounted = false; };
    }, [isEditMode, selectedBookListId, fetchBookListsId]);

    const findStudentOption = useCallback((studentId) => {
        if (!studentId) return null;
        return studentOptions.find(opt => `${opt.value}` === `${studentId}`) || null;
    }, [studentOptions]);

    const findBookOption = useCallback((bookId) => {
        if (!bookId) return null;
        return bookOptions.find(opt => `${opt.value}` === `${bookId}`) || null;
    }, [bookOptions]);

    const resolvedBookList = useMemo(() => {
        if (isEditMode && detailData?.bookLists && detailData.bookLists.length) {
            return detailData.bookLists[0];
        }
        // If editing and we have a selected book list from the dropdown
        if (isEditMode && selectedBookListId && selectedBookListId !== -1) {
            const found = studentBookLists.find(bl => parseInt(bl.id) === selectedBookListId);
            if (found) return found;
        }
        // For create mode, if bookList has a student property, use it
        if (!isEditMode && bookList && bookList.student) {
            return { student: bookList.student };
        }
        return bookList || null;
    }, [isEditMode, detailData, bookList, selectedBookListId, studentBookLists]);

    const resolvedItems = useMemo(() => {
        if (isEditMode && detailData?.bookListItems && detailData.bookListItems.length) {
            return detailData.bookListItems.map(item => ({
                rowId: generateRowId(),
                book: findBookOption(item.book_id) || null,
                dateAssigned: item.date_assigned && item.date_assigned !== 'null' ? item.date_assigned.slice(0, 10) : '',
                dateCompleted: item.date_completed && item.date_completed !== 'null' ? item.date_completed.slice(0, 10) : '',
                sortOrder: item.sort_order || ''
            }));
        }
        return [{
            rowId: generateRowId(),
            book: null,
            dateAssigned: '',
            dateCompleted: '',
            sortOrder: ''
        }];
    }, [isEditMode, detailData, findBookOption]);

    useEffect(() => {
        setItemRows(resolvedItems);
    }, [resolvedItems]);

    // Get selected students from bookList (for group assignments)
    const selectedStudents = useMemo(() => {
        if (!bookList) return [];
        if (bookList.groupStudentIds && Array.isArray(bookList.groupStudentIds)) {
            return bookList.groupStudentIds.map(studentId => {
                const student = students.find(s => s.user_id === studentId);
                return student ? `${student.first_name} ${student.last_name}` : `Student ${studentId}`;
            });
        } else if (bookList.student) {
            const student = students.find(s => s.user_id === bookList.student);
            return student ? [`${student.first_name} ${student.last_name}`] : [];
        }
        return [];
    }, [bookList, students]);

    // Template options for dropdown
    const templateOptions = useMemo(() => {
        return [
            { value: -1, label: 'Please select a template...' },
            { value: 'new', label: '+ New Book List Template' },
            ...templates.filter(t => parseInt(t.active) === 1).map(t => ({
                value: parseInt(t.id),
                label: t.name || 'Unnamed Template',
                obj: t
            }))
        ];
    }, [templates]);

    // Handle template selection - populate books table
    const handleTemplateChange = useCallback(async (selectedTemplate) => {
        if (!selectedTemplate || selectedTemplate.value === -1 || selectedTemplate.value === 'new') {
            return;
        }

        const templateId = parseInt(selectedTemplate.value);
        const template = templates.find(t => parseInt(t.id) === templateId);
        if (!template) return;

        // Get template items
        const items = templateItems
            .filter(item => parseInt(item.book_list_template_id) === templateId)
            .sort((a, b) => (parseInt(a.sort_order) || 0) - (parseInt(b.sort_order) || 0));

        // Enhance items with book info
        const enhancedItems = items.map((item, idx) => {
            const book = books.find(b => {
                const bookId = b.id || b.book_id;
                const itemBookId = item.book_id;
                if (!bookId || !itemBookId) return false;
                return parseInt(bookId) === parseInt(itemBookId) || String(bookId) === String(itemBookId);
            });

            return {
                rowId: generateRowId(),
                book: findBookOption(item.book_id),
                dateAssigned: '',
                dateCompleted: '',
                sortOrder: item.sort_order || idx + 1
            };
        });

        setItemRows(enhancedItems);
    }, [templates, templateItems, books, findBookOption]);

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedOverIndex, setDraggedOverIndex] = useState(null);

    // Drag and drop handlers
    const handleDragStart = useCallback((e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDraggedOverIndex(index);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggedItem(null);
        setDraggedOverIndex(null);
    }, []);

    const handleDrop = useCallback((e, dropIndex) => {
        e.preventDefault();
        if (draggedItem === null || draggedItem === dropIndex) {
            setDraggedItem(null);
            setDraggedOverIndex(null);
            return;
        }

        setItemRows(prev => {
            const items = [...prev];
            const draggedItemData = items[draggedItem];
            items.splice(draggedItem, 1);
            items.splice(dropIndex, 0, draggedItemData);

            // Update sort orders
            items.forEach((item, idx) => {
                item.sortOrder = idx + 1;
            });

            return items;
        });

        setDraggedItem(null);
        setDraggedOverIndex(null);
    }, [draggedItem]);

    const initialValues = {
        student: findStudentOption(resolvedBookList?.student) || null,
        name: resolvedBookList?.name || '',
        notes: resolvedBookList?.notes || '',
        active: parseInt(resolvedBookList?.active ?? 1) === 1,
        items: resolvedItems,
        selectedTemplate: { value: -1, label: 'Please select a template...' }
    };

    const modalTitle = isEditMode
        ? `Edit Book List${resolvedBookList?.name ? ` - ${resolvedBookList.name}` : ''}`
        : isDeleteMode
            ? `Delete Book List${bookList?.name ? ` - ${bookList.name}` : ''}`
            : 'Add Book List';

    const closeModal = (shouldRefresh = false) => {
        if (onClose) onClose(shouldRefresh);
    };

    const syncItemsToFormik = useCallback((rows, setFieldValue) => {
        setFieldValue('items', rows, false);
    }, []);

    const handleItemChange = useCallback((index, field, value, formik) => {
        setItemRows(prev => {
            const next = prev.map((row, i) =>
                i === index ? { ...row, [field]: value } : row
            );
            syncItemsToFormik(next, formik.setFieldValue);
            return next;
        });
    }, [syncItemsToFormik]);

    const handleAddItem = useCallback((formik) => {
        setItemRows(prev => {
            const next = [...prev, {
                rowId: generateRowId(),
                book: null,
                dateAssigned: '',
                dateCompleted: '',
                sortOrder: ''
            }];
            syncItemsToFormik(next, formik.setFieldValue);
            return next;
        });
    }, [syncItemsToFormik]);

    const handleRemoveItem = useCallback((index, formik) => {
        setItemRows(prev => {
            let next = prev.filter((_, i) => i !== index);
            if (!next.length) {
                next = [{
                    rowId: generateRowId(),
                    book: null,
                    dateAssigned: '',
                    dateCompleted: '',
                    sortOrder: ''
                }];
            }
            syncItemsToFormik(next, formik.setFieldValue);
            return next;
        });
    }, [syncItemsToFormik]);

    const buildPayload = (values) => {
        const sanitizedItems = (values.items || [])
            .map((item, index) => ({
                bookId: item.book?.value,
                dateAssigned: item.dateAssigned || null,
                dateCompleted: item.dateCompleted || null,
                sortOrder: item.sortOrder ? parseInt(item.sortOrder, 10) : index + 1
            }))
            .filter(item => item.bookId);

        return {
            bookList: {
                id: resolvedBookList?.id,
                studentId: values.student?.value,
                name: values.name.trim(),
                notes: values.notes?.trim() || '',
                active: values.active ? 1 : 0
            },
            bookListItems: sanitizedItems
        };
    };

    const handleSubmit = async (values, formikHelpers) => {
        const { setSubmitting, setStatus } = formikHelpers;
        setStatus(null);
        setActionError(null);

        // Use itemRows instead of values.items for payload
        const valuesWithItems = { ...values, items: itemRows };
        const payload = buildPayload(valuesWithItems);
        if (!payload.bookListItems.length) {
            setStatus('Add at least one book to this list.');
            setSubmitting(false);
            return;
        }

        try {
            // Check if this is a group assignment (bookList has groupStudentIds)
            const groupStudentIds = bookList?.groupStudentIds;

            if (groupStudentIds && Array.isArray(groupStudentIds) && groupStudentIds.length > 0) {
                // Create book list for all students in the group
                let successCount = 0;
                let errorCount = 0;
                const errors = [];

                for (const studentId of groupStudentIds) {
                    try {
                        const groupPayload = {
                            ...payload,
                            bookList: {
                                ...payload.bookList,
                                studentId: studentId
                            }
                        };
                        const response = await createBookList(groupPayload);
                        if (response && response.status < 400) {
                            successCount++;
                        } else {
                            errorCount++;
                            errors.push(`Failed for student ${studentId}`);
                        }
                    } catch (error) {
                        errorCount++;
                        errors.push(`Error for student ${studentId}: ${error.message}`);
                    }
                }

                if (errorCount > 0) {
                    setStatus(`Created for ${successCount} students, but ${errorCount} failed. ${errors.join('; ')}`);
                    // Still refresh if at least one succeeded, so successful book lists show up
                    if (successCount > 0) {
                        setSubmitting(false);
                        setSubmitted(true);
                        setSubmissionStatus('created');
                        // Close modal and trigger refresh after a short delay to show status message
                        setTimeout(() => {
                            closeModal(true);
                        }, 2000);
                    } else {
                        setSubmitting(false);
                    }
                } else {
                    setSubmitting(false);
                    setSubmitted(true);
                    setSubmissionStatus('created');
                    // Close modal and trigger refresh
                    setTimeout(() => {
                        closeModal(true);
                    }, 1000);
                }
            } else {
                // Single student assignment
                let response = null;
                if (isEditMode) {
                    response = await updateBookList(payload);
                } else {
                    response = await createBookList(payload);
                }
                if (!response || response.status >= 400) {
                    setStatus(response?.data?.message || 'Unable to save book list. Please try again.');
                    setSubmitting(false);
                    return;
                }
                setSubmitting(false);
                setSubmitted(true);
                setSubmissionStatus(isEditMode ? 'updated' : 'created');
            }
        } catch (error) {
            console.error('[BOOK LIST MODAL] Error saving book list:', error);
            setStatus('Unexpected error while saving. Please try again.');
            setSubmitting(false);
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: handleSubmit
    });

    // Fetch book lists for selected student (when editing) - moved after formik definition
    useEffect(() => {
        let isMounted = true;
        if (!isEditMode || !formik.values.student?.value || formik.values.student?.value === -1) {
            setStudentBookLists([]);
            return () => { isMounted = false; };
        }

        (async () => {
            try {
                const response = await fetchBookListsStudents({
                    studentIds: [formik.values.student.value]
                });
                if (!isMounted) return;

                let bookLists = [];
                if (response?.data?.bookLists) {
                    bookLists = response.data.bookLists;
                } else {
                    bookLists = getDataArray(response);
                }

                if (isMounted) {
                    setStudentBookLists(bookLists);
                    // If no book list is selected yet, select the first one
                    if (!selectedBookListId && bookLists.length > 0) {
                        setSelectedBookListId(parseInt(bookLists[0].id));
                    }
                }
            } catch (error) {
                console.error('[BOOK LIST MODAL] Error fetching student book lists:', error);
                if (isMounted) setStudentBookLists([]);
            }
        })();

        return () => { isMounted = false; };
    }, [isEditMode, formik.values.student?.value, fetchBookListsStudents, selectedBookListId]);

    const handleDelete = async () => {
        if (!bookList?.id) {
            setActionError('Missing book list ID.');
            return;
        }
        setDeleteProcessing(true);
        setActionError(null);
        try {
            const response = await deleteBookList({ id: bookList.id });
            if (!response || response.status >= 400) {
                setActionError(response?.data?.message || 'Unable to delete book list.');
                setDeleteProcessing(false);
                return;
            }
            setDeleteProcessing(false);
            setSubmitted(true);
            setSubmissionStatus('deleted');
        } catch (error) {
            console.error('[BOOK LIST MODAL] Error deleting book list:', error);
            setDeleteProcessing(false);
            setActionError('Unexpected error while deleting the book list.');
        }
    };

    const renderSuccess = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4 className="mb-2 text-mpBlue">
                    {submissionStatus === 'deleted'
                        ? 'Book list deleted successfully.'
                        : `Book list ${submissionStatus} successfully.`}
                </h4>
                <div>You can close this dialog to refresh the latest data.</div>
            </Modal.Body>
            <Modal.Footer>
                <Button color="mpBlue" onClick={() => closeModal(true)}>
                    Close
                </Button>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const renderDelete = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4 className="text-mpLRed mb-2">Delete this book list?</h4>
                <p>
                    This action cannot be undone.
                    {bookList?.name ? ` (${bookList.name})` : ''}
                </p>
                {actionError && (
                    <>
                        <br />
                        <ErrorMessage color="mpLRed">{actionError}</ErrorMessage>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex flex-row gap-x-2 flex-wrap items-center">
                    {deleteProcessing && <ModalProcessing />}
                    <Button
                        color="mpLRed"
                        onClick={() => closeModal(false)}
                        disabled={deleteProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="hol-mpLRed"
                        onClick={handleDelete}
                        disabled={deleteProcessing}
                    >
                        Delete
                    </Button>
                </div>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const renderLoading = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4>Loading book list...</h4>
                <p>Please wait while we load the latest data.</p>
                {detailError && (
                    <>
                        <br />
                        <ErrorMessage color="mpLRed">{detailError}</ErrorMessage>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex flex-row gap-x-2 flex-wrap items-center">
                    {detailLoading && <ModalProcessing />}
                    <Button color="mpBlue" onClick={() => closeModal(false)}>
                        Close
                    </Button>
                </div>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const renderForm = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <div className="grid grid-cols-1 gap-y-4">
                    {/* Show selected student(s) at top - greyed out, cannot be changed */}
                    {!isEditMode && selectedStudents.length > 0 && (
                        <div className="bg-gray-100 p-3 rounded-md">
                            <label className="text-[#0000008A] text-[10pt] mb-1 block font-semibold">Selected Student(s)</label>
                            <div className="text-gray-600">
                                {selectedStudents.map((name, idx) => (
                                    <div key={idx} className="py-1">{name}</div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Edit mode: Student and Book List selection */}
                    {isEditMode && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-[#0000008A] text-[10pt] mb-1 block">Student</label>
                                <SelectSingle
                                    id="book-list-student"
                                    name="student"
                                    placeholder="Select a student"
                                    options={studentOptions}
                                    value={formik.values.student}
                                    onChange={(e) => {
                                        formik.setFieldValue('student', e.target.value);
                                        setSelectedBookListId(null);
                                        setStudentBookLists([]);
                                        setDetailData(null);
                                        setItemRows([{
                                            rowId: generateRowId(),
                                            book: null,
                                            dateAssigned: '',
                                            dateCompleted: '',
                                            sortOrder: ''
                                        }]);
                                    }}
                                />
                                {formik.errors.student && (
                                    <ErrorMessage color="mpLRed">
                                        {formik.errors.student}
                                    </ErrorMessage>
                                )}
                            </div>
                            {formik.values.student?.value && studentBookLists.length > 0 && (
                                <div>
                                    <label className="text-[#0000008A] text-[10pt] mb-1 block">Book List</label>
                                    <SelectSingle
                                        id="book-list-select"
                                        name="selectedBookList"
                                        placeholder="Select a book list"
                                        options={bookListOptions}
                                        value={bookListOptions.find(opt => opt.value === selectedBookListId) || bookListOptions[0]}
                                        onChange={(e) => {
                                            setSelectedBookListId(e.target.value?.value || null);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Create mode: Book List Template dropdown */}
                    {!isEditMode && (
                        <div>
                            <label className="text-[#0000008A] text-[10pt] mb-1 block">Book List Template</label>
                            <SelectSingle
                                id="book-list-template"
                                name="selectedTemplate"
                                placeholder="Select a template to populate books"
                                options={templateOptions}
                                value={formik.values.selectedTemplate}
                                onChange={async (e) => {
                                    const selectedTemplate = e.target.value;
                                    formik.setFieldValue('selectedTemplate', selectedTemplate);

                                    if (selectedTemplate.value === 'new') {
                                        // Open template modal - handled by parent
                                        return;
                                    }

                                    await handleTemplateChange(selectedTemplate);
                                }}
                            />
                        </div>
                    )}

                    {/* List Name - single instance */}
                    {(!isEditMode || (isEditMode && formik.values.student?.value && selectedBookListId && selectedBookListId !== -1)) && (
                        <div>
                            <FormikControl
                                id="book-list-name"
                                name="name"
                                label="Book List Name"
                                placeholder="Enter list name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                disabled={isEditMode && selectedBookListId && selectedBookListId !== -1}
                            />
                            {formik.errors.name && (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.name}
                                </ErrorMessage>
                            )}
                        </div>
                    )}

                    {isEditMode && formik.values.student?.value && studentBookLists.length === 0 && (
                        <div className="text-sm text-gray-500">
                            No book lists found for this student. Create a new one or select a different student.
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-y-2">
                        <FormikControl
                            id="book-list-notes"
                            name="notes"
                            as="textarea"
                            rows="3"
                            label="Notes"
                            placeholder="Internal notes..."
                            value={formik.values.notes}
                            onChange={formik.handleChange}
                        />
                    </div>
                    <div>
                        <Check
                            color="mpBlue"
                            name="active"
                            label="Book list is active"
                            checked={formik.values.active}
                            onChange={formik.handleChange}
                        />
                    </div>
                    <div className="border border-mpLGrey-4 rounded-md p-4">
                        <div className="flex flex-row justify-between items-center mb-3 flex-wrap gap-2">
                            <h4 className="text-lg font-semibold">Books in this list</h4>
                            <Button
                                color="lte-mpLBlue"
                                onClick={() => handleAddItem(formik)}
                            >
                                + Add Book
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {itemRows.map((item, index) => {
                                const itemErrors = formik.errors.items?.[index] || {};
                                return (
                                    <div
                                        key={item.rowId || index}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDrop={(e) => handleDrop(e, index)}
                                        className={`grid grid-cols-1 md:grid-cols-12 gap-3 border border-mpLGrey-4 rounded-md p-3 cursor-move hover:bg-gray-50 ${draggedItem === index ? 'opacity-50' : ''
                                            } ${draggedOverIndex === index ? 'bg-blue-100 border-2 border-blue-400' : ''
                                            }`}
                                    >
                                        {/* Drag handle indicator */}
                                        <div className="md:col-span-1 flex items-center justify-center text-gray-400">
                                            <i className="fas fa-grip-vertical"></i>
                                        </div>
                                        <div className="md:col-span-3">
                                            <label className="text-[#0000008A] text-[10pt] mb-1 block">
                                                Book
                                            </label>
                                            <SelectSingle
                                                id={`book-list-item-book-${index}`}
                                                name={`items[${index}].book`}
                                                placeholder="Select book"
                                                options={bookOptions}
                                                value={item.book}
                                                onChange={(e) => handleItemChange(index, 'book', e.target.value, formik)}
                                            />
                                            {itemErrors.book && (
                                                <ErrorMessage color="mpLRed">
                                                    {itemErrors.book}
                                                </ErrorMessage>
                                            )}
                                        </div>
                                        <div className="md:col-span-2">
                                            <FormikControl
                                                id={`book-list-item-dateAssigned-${index}`}
                                                name={`items[${index}].dateAssigned`}
                                                label="Date Assigned"
                                                type="date"
                                                value={item.dateAssigned || ''}
                                                onChange={(e) => handleItemChange(index, 'dateAssigned', e.target.value, formik)}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <FormikControl
                                                id={`book-list-item-dateCompleted-${index}`}
                                                name={`items[${index}].dateCompleted`}
                                                label="Date Completed"
                                                type="date"
                                                value={item.dateCompleted || ''}
                                                onChange={(e) => handleItemChange(index, 'dateCompleted', e.target.value, formik)}
                                            />
                                        </div>
                                        <div className="md:col-span-1">
                                            <FormikControl
                                                id={`book-list-item-sort-${index}`}
                                                name={`items[${index}].sortOrder`}
                                                label="Sort Order"
                                                type="number"
                                                min="1"
                                                placeholder={String(index + 1)}
                                                value={item.sortOrder}
                                                onChange={(e) => handleItemChange(index, 'sortOrder', e.target.value, formik)}
                                            />
                                            {itemErrors.sortOrder && (
                                                <ErrorMessage color="mpLRed">
                                                    {itemErrors.sortOrder}
                                                </ErrorMessage>
                                            )}
                                        </div>
                                        <div className="md:col-span-1 flex items-end">
                                            {itemRows.length > 1 && (
                                                <Button
                                                    color="mpLRed"
                                                    onClick={() => handleRemoveItem(index, formik)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex flex-row gap-x-2 flex-wrap items-center">
                    {formik.isSubmitting && <ModalProcessing />}
                    {formik.status && (
                        <ErrorMessage color="mpLRed">
                            {formik.status}
                        </ErrorMessage>
                    )}
                    <Button
                        color="mpLRed"
                        onClick={() => closeModal(false)}
                        disabled={formik.isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="mpBlue"
                        onClick={formik.handleSubmit}
                        disabled={formik.isSubmitting}
                    >
                        {isEditMode ? 'Save Changes' : 'Add Book List'}
                    </Button>
                </div>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    // Determine which content to show - must be after all render functions are defined
    const modalContent = submitted
        ? renderSuccess()
        : isDeleteMode
            ? renderDelete()
            : detailLoading || detailError
                ? renderLoading()
                : renderForm();

    return (
        <Modal
            show={show}
            onHide={() => closeModal(false)}
            preventClickout={false}
            className="w-11/12 md:w-3/4 xl:w-7/12"
        >
            <Modal.Header>
                <h2>{modalTitle}</h2>
            </Modal.Header>
            {modalContent}
        </Modal>
    );
}

export default BookListModal;


